import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ConsentToggles } from "../features/consent/ConsentToggles";
import { LANG_STORAGE_KEY } from "../lib/i18n";
import { safeGet, safeSet } from "../lib/safeStorage";

type Lang = "es" | "en";

function readLang(): Lang {
  return safeGet(LANG_STORAGE_KEY) === "en" ? "en" : "es";
}

export function SettingsRoute() {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState<Lang>(readLang);

  function pick(next: Lang) {
    setLang(next);
    safeSet(LANG_STORAGE_KEY, next);
    void i18n.changeLanguage(next);
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <h1 className="text-2xl font-bold text-fg">{t("settings.title")}</h1>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-fg-mute">{t("settings.language_heading")}</h2>
        <div className="mt-2 flex flex-wrap gap-2">
          <LangButton choice="es" current={lang} onPick={pick}>
            {t("settings.language_es")}
          </LangButton>
          <LangButton choice="en" current={lang} onPick={pick}>
            {t("settings.language_en")}
          </LangButton>
        </div>
      </section>

      <ConsentToggles />
    </div>
  );
}

function LangButton({
  choice,
  current,
  onPick,
  children,
}: {
  choice: Lang;
  current: Lang;
  onPick: (c: Lang) => void;
  children: React.ReactNode;
}) {
  const selected = choice === current;
  return (
    <button
      type="button"
      onClick={() => onPick(choice)}
      aria-pressed={selected}
      className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
        selected
          ? "border-volt bg-bg-deep text-fg"
          : "border-line bg-surface text-fg-mute hover:border-line-strong"
      }`}
    >
      {children}
    </button>
  );
}
